<template>
  <div
    class="brand-card"
    @click="handleViewDetails(brand)"
  >
    <div class="brand-header">
      <div class="brand-logo">
        <img
          :src="brand.logo"
          :alt="brand.name"
          @error="handleImageError"
        >
      </div>
      <div
        v-if="brand.featured"
        class="brand-badge"
      >
        <van-icon name="fire" />
        {{ $t('brands.featured') }}
      </div>
    </div>

    <div class="brand-content">
      <h3 class="brand-name">
        {{ brand.name }}
      </h3>
      <p
        v-if="brand.description"
        class="brand-desc"
      >
        {{ brand.description }}
      </p>

      <div class="brand-stats">
        <div class="stat">
          <div class="stat-value">
            {{ brand.productCount || 0 }}+
          </div>
          <div class="stat-label">
            {{ $t('brands.products') }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-value">
            {{ brand.verificationCount || 0 }}+
          </div>
          <div class="stat-label">
            {{ $t('brands.verifications') }}
          </div>
        </div>
      </div>

      <div class="brand-meta" v-if="brand.joinTime">
        <span class="join-date">
          <van-icon name="clock" />
          {{$t('brands.establishTime')}} {{ formatDate(brand.establishTime) }}
        </span>
      </div>
    </div>

    <div class="brand-actions">
<!--      <van-button-->
<!--        size="small"-->
<!--        type="primary"-->
<!--        plain-->
<!--        @click.stop="handleViewDetails(brand)"-->
<!--      >-->
<!--        {{ $t('brands.viewDetails') }}-->
<!--      </van-button>-->
    </div>
  </div>
</template>

<script lang="ts">
import { useRouter } from 'vue-router'
import type { Brand } from '@/api/brand'
import { getUserLang } from '@/storage/user'
export default {
  name: 'BrandCard',
  emits: ['click'],
  props: {
    brand: {
      type: Object,
      required: true
    }
  },
  setup () {
    const router = useRouter()

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement
      target.src = 'https://via.placeholder.com/200x100/0A1E3C/FFFFFF?text=Brand+Logo'
    }

    const formatDate = (dateString: string) => {
      const date = new Date(dateString)
      const lang = getUserLang()
      return date.toLocaleDateString(lang, {
        year: 'numeric'
        // month: 'short'
      })
    }

    const handleViewDetails = (brand: Brand) => {
      // 这里可以跳转到品牌详情页
      router.push({
        path: '/brand/detail',
        query: {
          brandId: brand.id
        }
      })
    }
    return {
      handleImageError,
      formatDate,
      handleViewDetails
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.brand-card {
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: $card-shadow;
  overflow: hidden;
  transition: all $transition-normal;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(10, 30, 60, 0.15);
  }
}

.brand-header {
  position: relative;
  padding: $spacing-lg;
  text-align: center;
  border-bottom: 1px solid rgba($light-gray, 0.5);
}

.brand-logo {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
}

.brand-badge {
  position: absolute;
  top: $spacing-md;
  right: $spacing-md;
  background: linear-gradient(135deg, $accent-gold 0%, darken($accent-gold, 10%) 100%);
  color: $white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: $font-size-xs;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.brand-content {
  padding: $spacing-lg;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-size: $font-size-lg;
  font-weight: 600;
  margin-bottom: $spacing-sm;
  color: $primary-blue;
}

.brand-desc {
  color: $text-light;
  font-size: $font-size-sm;
  line-height: 1.4;
  margin-bottom: $spacing-lg;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.brand-stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: $spacing-lg;
  padding: $spacing-md 0;
  border-top: 1px solid rgba($light-gray, 0.5);
  border-bottom: 1px solid rgba($light-gray, 0.5);
}

.stat {
  text-align: center;
}

.stat-value {
  font-size: $font-size-lg;
  font-weight: 700;
  color: $accent-gold;
  margin-bottom: 2px;
}

.stat-label {
  font-size: $font-size-xs;
  color: $text-light;
  text-transform: uppercase;
}

.brand-meta {
  margin-top: auto;
}

.join-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: $font-size-xs;
  color: $text-light;

  .van-icon {
    font-size: 12px;
  }
}

.brand-actions {
  padding: 0 $spacing-lg $spacing-lg;
  text-align: center;
}

// 响应式设计
@media (max-width: $breakpoint-md) {
  .brand-header {
    padding: $spacing-md;
  }

  .brand-logo {
    height: 60px;
  }

  .brand-content {
    padding: $spacing-md;
  }

  .brand-name {
    font-size: $font-size-md;
  }

  .brand-desc {
    font-size: $font-size-xs;
    margin-bottom: $spacing-md;
  }

  .brand-stats {
    margin-bottom: $spacing-md;
  }

  .stat-value {
    font-size: $font-size-md;
  }
}
</style>
